/**
 * -----------------------------------------------------------------------------
 * NCJ COMPONENT: Delivery Estimator Logic
 * FILE: ncj-delivery-estimator.js
 * PURPOSE: Shared logic for estimated delivery dates and countdown timers.
 * AUTHOR: No Code Jungle (NCJ)
 * -----------------------------------------------------------------------------
 */

(function () {
    if (window.ncjDeliveryEstimatorInitialized) return;
    window.ncjDeliveryEstimatorInitialized = true;

    const CONFIG_DEFAULTS = {
        resetHour: 19,
        locales: {
            en: { months: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"], dateOrder: 'MD', preposition: ' ' },
            es: { months: ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"], dateOrder: 'DM', preposition: ' de ' },
            fr: { months: ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"], dateOrder: 'DM', preposition: ' ' },
            de: { months: ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"], dateOrder: 'DM', preposition: ' ' },
            it: { months: ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"], dateOrder: 'DM', preposition: ' ' },
            nl: { months: ["januari", "februari", "maart", "april", "mei", "juni", "juli", "augustus", "september", "oktober", "november", "december"], dateOrder: 'DM', preposition: ' ' },
            pl: { months: ["stycznia", "lutego", "marca", "kwietnia", "maja", "czerwca", "lipca", "sierpnia", "września", "października", "listopada", "grudnia"], dateOrder: 'DM', preposition: ' ' },
            'pt-PT': { months: ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "settembre", "outubro", "novembro", "dezembro"], dateOrder: 'DM', preposition: ' de ' },
            bg: { months: ["януари", "февруари", "март", "април", "май", "юни", "юли", "август", "септември", "октомври", "ноември", "декември"], dateOrder: 'DM', preposition: ' ' },
            da: { months: ["januar", "februar", "marts", "april", "maj", "juni", "juli", "august", "september", "oktober", "november", "december"], dateOrder: 'DM', preposition: ' ' },
            el: { months: ["Ιανουαρίου", "Φεβρουαρίου", "Μαρτίου", "Απριλίου", "Μαΐου", "Ιουνίου", "Ιουλίου", "Αυγούστου", "Σεπτεμβρίου", "Οκτωβρίου", "Νοεμβρίου", "Δεκεμβρίου"], dateOrder: 'DM', preposition: ' ' },
            ro: { months: ["ianuarie", "februarie", "martie", "aprilie", "mai", "iunie", "iulie", "august", "septembrie", "octombrie", "noiembrie", "decembrie"], dateOrder: 'DM', preposition: ' ' },
            cs: { months: ["ledna", "února", "března", "dubna", "května", "června", "července", "srpna", "září", "října", "listopadu", "prosince"], dateOrder: 'DM', preposition: ' ' }
        }
    };

    function getLocaleConfig() {
        const lang = document.documentElement.lang || 'en';
        return CONFIG_DEFAULTS.locales[lang] || CONFIG_DEFAULTS.locales[lang.split('-')[0]] || CONFIG_DEFAULTS.locales.en;
    }

    function getLocalizedMonth(date) {
        const config = getLocaleConfig();
        return config.months[date.getMonth()];
    }

    function formatSingleDate(dateStr) {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '';
        const day = date.getDate();
        const month = getLocalizedMonth(date);
        const year = date.getFullYear();
        const currentYear = new Date().getFullYear();
        const config = getLocaleConfig();
        const yearPart = year !== currentYear ? ' ' + year : '';

        if (config.dateOrder === 'DM') {
            return `${day}${config.preposition}${month}${yearPart}`;
        } else {
            return `${month} ${day}${yearPart}`;
        }
    }

    function formatDateRange(endDateStr) {
        if (!endDateStr) return '';
        const endDate = new Date(endDateStr);
        if (isNaN(endDate.getTime())) return '';
        const startDate = new Date(endDate);
        startDate.setDate(endDate.getDate() - 4);
        const startDay = startDate.getDate();
        const endDay = endDate.getDate();
        const startMonth = getLocalizedMonth(startDate);
        const endMonth = getLocalizedMonth(endDate);
        const startYear = startDate.getFullYear();
        const endYear = endDate.getFullYear();
        const currentYear = new Date().getFullYear();
        const config = getLocaleConfig();
        const yearPart = (endYear !== currentYear) ? ', ' + endYear : '';

        if (config.dateOrder === 'DM') {
            if (startYear !== endYear) {
                return `${startDay}${config.preposition}${startMonth}, ${startYear} – ${endDay}${config.preposition}${endMonth}, ${endYear}`;
            } else if (startMonth !== endMonth) {
                return `${startDay}${config.preposition}${startMonth} – ${endDay}${config.preposition}${endMonth}${yearPart}`;
            } else {
                return `${startDay}–${endDay}${config.preposition}${endMonth}${yearPart}`;
            }
        } else {
            if (startYear !== endYear) {
                return `${startMonth} ${startDay}, ${startYear} – ${endMonth} ${endDay}, ${endYear}`;
            } else if (startMonth !== endMonth) {
                return `${startMonth} ${startDay} – ${endMonth} ${endDay}${yearPart}`;
            } else {
                return `${startMonth} ${startDay}–${endDay}${yearPart}`;
            }
        }
    }

    window.updateEstimatorNcj = function (container) {
        try {
            const sku = container.getAttribute('data-sku');
            if (!sku) return;
            const isPdf = sku.includes('-2');
            const context = container.getAttribute('data-context');
            const mainTarget = container.querySelector('[data-estimator-main-target]');

            // Get translations (Check inside first, then sibling)
            const stringsEl = container.querySelector('[data-estimator-strings]') ||
                (container.previousElementSibling && container.nextElementSibling.matches('[data-estimator-strings]') ? container.nextElementSibling : null) ||
                document.querySelector(`[data-estimator-strings-for="${container.id}"]`);

            if (!stringsEl) return;
            const strings = JSON.parse(stringsEl.textContent);

            let toggle;
            if (context === 'vincheck') {
                const wrapper = container.closest('.ncj-vin-check-results-wrapper');
                toggle = wrapper ? wrapper.querySelector('.addon-toggle-input') : null;
            } else {
                toggle = document.querySelector('.ncj-addon-input');
            }

            const isExpress = toggle ? toggle.checked : false;
            let dateHtml = '';
            let template = '';

            if (isPdf) {
                dateHtml = formatSingleDate(container.getAttribute('data-pdf-date'));
                template = strings.templates.pdf;
            } else {
                if (isExpress) {
                    dateHtml = formatSingleDate(container.getAttribute('data-express-date'));
                    template = strings.templates.express;
                } else {
                    dateHtml = formatDateRange(container.getAttribute('data-standard-date'));
                    template = strings.templates.standard;
                }
            }

            if (mainTarget && dateHtml) {
                const dateSpan = `<span class="ncj-delivery-estimator__date">${dateHtml}</span>`;
                const message = template.replace('[date]', dateSpan).replace('el <span', '<span');

                // Preserve the existing timer row to avoid breaking its listeners/state
                let timerRow = mainTarget.querySelector('.ncj-delivery-estimator__timer-span');
                if (timerRow) {
                    const timerHtml = timerRow.outerHTML.trim();
                    mainTarget.innerHTML = (message.trim() + timerHtml).replace(/\s+\./g, '.');
                } else {
                    // Fallback if timerRow is missing (should not happen normally)
                    mainTarget.innerHTML = message.trim().replace(/\s+\./g, '.');
                }
            }
        } catch (e) {
            console.error('NCJ Delivery Estimator Update Error:', e);
        }
    };

    window.startCountdownNcj = function (container) {
        try {
            const stringsEl = container.querySelector('[data-estimator-strings]') ||
                (container.nextElementSibling && container.nextElementSibling.matches('[data-estimator-strings]') ? container.nextElementSibling : null) ||
                document.querySelector(`[data-estimator-strings-for="${container.id}"]`);

            if (!stringsEl) return;
            const strings = JSON.parse(stringsEl.textContent);

            function tick() {
                try {
                    // Always re-look up the target to handle DOM updates (e.g. variant change)
                    const countdownTarget = container.querySelector('[data-estimator-countdown-target]');
                    if (!countdownTarget) return;

                    const now = new Date();
                    const madridTime = new Date(now.toLocaleString("en-US", { timeZone: "Europe/Madrid" }));
                    let resetTime = new Date(madridTime);
                    resetTime.setHours(CONFIG_DEFAULTS.resetHour, 0, 0, 0);
                    if (madridTime >= resetTime) resetTime.setDate(resetTime.getDate() + 1);
                    const diff = resetTime - madridTime;
                    const hours = Math.floor(diff / (1000 * 60 * 60));
                    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                    let displayHours = (hours === 0 && minutes === 0) ? 24 : hours;
                    let displayMinutes = (hours === 0 && minutes === 0) ? 0 : minutes;

                    countdownTarget.innerHTML = `${displayHours} <span class="ncj-delivery-estimator__countdown-unit">${strings.hoursLabel.trim()}</span> ${displayMinutes} <span class="ncj-delivery-estimator__countdown-unit">${strings.minutesLabel.trim()}</span>`.replace(/\s+/g, ' ').trim();
                } catch (e) { }
            }
            tick();
            if (!container.dataset.timerInterval) {
                container.dataset.timerInterval = setInterval(tick, 60000);
            }
        } catch (e) {
            console.error('NCJ Delivery Estimator Countdown Error:', e);
        }
    };

    window.initNcjDeliveryEstimator = function (container) {
        try {
            if (container.dataset.estimatorInitialized) {
                // If already initialized, just trigger an update (handles manual calls from renderer)
                window.updateEstimatorNcj(container);
                return;
            }

            const context = container.getAttribute('data-context');
            if (context === 'vincheck') {
                // Scoped direct binding: the vincheck toggle lives inside the results
                // wrapper and is initialized together with it, so no delegation needed.
                try {
                    const wrapper = container.closest('.ncj-vin-check-results-wrapper');
                    const toggle = wrapper ? wrapper.querySelector('.addon-toggle-input') : null;
                    if (toggle && !toggle.dataset.estimatorInit) {
                        toggle.addEventListener('change', () => window.updateEstimatorNcj(container));
                        toggle.dataset.estimatorInit = "true";
                    }
                } catch (e) { }
            } else {
                // Delegated listener: the toggle node can be replaced on variant change
                // (Section Rendering API re-render), which would drop a direct listener.
                // Delegation on document survives any re-render at zero idle cost —
                // unlike a body-wide MutationObserver, which fires on every DOM change.
                document.addEventListener('change', function (e) {
                    if (e.target && e.target.classList && e.target.classList.contains('ncj-addon-input')) {
                        window.updateEstimatorNcj(container);
                    }
                });
            }

            // Shopify Variant events (only for product page)
            if (context !== 'vincheck') {
                const handleVariant = (payload) => {
                    // Dawn sends payload directly or sometimes wrapped in 'data'
                    const variant = payload?.variant || payload?.data?.variant;
                    if (variant) {
                        container.setAttribute('data-sku', variant.sku);
                        window.updateEstimatorNcj(container);
                    }
                };

                if (typeof subscribe === 'function') {
                    // Dawn global pubsub
                    subscribe('variant-change', handleVariant);
                } else if (window.Shopify?.pubsub) {
                    window.Shopify.pubsub.subscribe('variant_change', handleVariant);
                }
            }

            window.updateEstimatorNcj(container);
            window.startCountdownNcj(container);
            container.dataset.estimatorInitialized = "true";
        } catch (e) {
            console.error('NCJ Delivery Estimator Init Error:', e);
        }
    };

    // Auto-init on load for static instances
    document.addEventListener('DOMContentLoaded', () => {
        document.querySelectorAll('.ncj-delivery-estimator').forEach(c => window.initNcjDeliveryEstimator(c));
    });
    // Fallback for already loaded DOM
    if (document.readyState !== 'loading') {
        document.querySelectorAll('.ncj-delivery-estimator').forEach(c => window.initNcjDeliveryEstimator(c));
    }
})();
